import PropTypes from 'prop-types'
import React from 'react'

import AButton from './AButton'

/**
This is used to generate all of our custom buttons for RA.  We currently have the following types of buttton:

1. Success button - orange background, white font, uses padding for width.  This is the default if you supply no props to this component.
1. Success button - 100% width, orange background, white font. Supply only usePadding = false
2. Info button - 100% width, white background, grey text.  Supply successButton = false to make this happen
3. Original Button selector button (used for search filter buttons for example) - uses small padding and margins.  white background and "black" (dark blue) text.  useGroupClass = true
4. Buttons and button selectors for forms - uses large padding and margins.  white background and "black" text.  useGroupClass = true, usePadding = true, formGroupPadding = true and formGroupMargin = true (if buttons will be vertically stacked in mobile mode or if you want space underneath the button bar)
5. Responsive buttons and button selectors - use min-width to fill the screen.  white background and "black" text.  usePadding = false and numColumns = number of buttons used to fill a row

@example ../../../../docs/external/button/AbuttonStandard.md
*/

const AButtonStandard = ({
  animate = false,
  animationClasses = 'fa fa-spinner fa-spin',
  children,
  className = '',
  disabled,
  formGroupMargin = false,
  formGroupPadding = false,
  numColumns = 1,
  onClick,
  selected = false,
  successButton = true,
  type = 'button',
  usePadding = true,
  useGroupClass = false,
  value,
  cmpRef,
  cmpLabel,
  cmpDescribedBy,
  fontSizeType = 1,
}) => {
  className += ' btn'
  if (useGroupClass) {
    className += selected
      ? ' btn-selection-group-selected'
      : ' btn-selection-group'
  } else {
    className += successButton ? ' btn-success' : ' btn-info'
  }

  if (formGroupPadding) {
    className += ' btn-selection-group-form-padding'
  }

  if (formGroupMargin) {
    className += ' btn-selection-group-form-margin'
  }

  if (usePadding) {
    className += ' btn-selection-group-width-default'
  } else {
    className += ` btn-selection-group-${
      formGroupPadding ? 'form-' : ''
    }width${numColumns}`
  }

  className += ` btn-font-size-${fontSizeType}`

  let animator = ''
  if (animate) {
    animator = (
      <span>
        <i className={animationClasses} />{' '}
      </span>
    )
  }

  return (
    <AButton
      cmpLabel={cmpLabel}
      cmpDescribedBy={cmpDescribedBy}
      cmpRef={cmpRef}
      className={className}
      disabled={disabled}
      onClick={onClick}
      type={type}
      value={value}
    >
      {animator}
      {children}
    </AButton>
  )
}

AButtonStandard.propTypes = {
  /** set to true to add a spinner icon to the button */
  animate: PropTypes.bool,
  /** pass in your own animation classes instead of the default spinner */
  animationClasses: PropTypes.string,
  /** usually just the button label */
  children: PropTypes.node,
  /** custom style applied to the button */
  className: PropTypes.string,
  /** whether or not button is disabled */
  disabled: PropTypes.bool,
  /** formGroupMargin - use this when formGroupPadding is true and you have more than one button i.e. you are using a buttonselector along with formGroupPadding */
  formGroupMargin: PropTypes.bool,
  /** use this when the button appears on a form where the standard padding of "padding: 10px 20px;" is used.  If this is not specified the original default of "padding: 0px 6px;" will be used. */
  formGroupPadding: PropTypes.bool,
  /** a number from 1 to 4 which will specify the width of the button i.e. 1 = 100%, 2 = 50%, 3 = 33%, 4 = 25% (approximately - see css bootstrap.less).  Note: in order for this to take affect, usePadding must be set to false */
  numColumns: PropTypes.oneOf([1, 2, 3, 4]),
  /** function to run when clicked */
  onClick: PropTypes.func,
  /** orange text and border when set to true and useGroupClass is set to true */
  selected: PropTypes.bool,
  /** styling for the positive/default action. */
  successButton: PropTypes.bool,
  /** clickable button, submit - submits a form, reset - reset form data to initial values */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** set this to true to use the styling that is reserved for our buttonselector */
  useGroupClass: PropTypes.bool,
  /** if this is true, the width of the button will be determined by the length of the word within it.  If this is false, then the length of the button will be determined by numColumns */
  usePadding: PropTypes.bool,
  /** value when button is pushed */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** unique reference for component on the DOM */
  cmpRef: PropTypes.string,
  /** accessibility label */
  cmpLabel: PropTypes.string,
  /** accessibility described by used especially for button selector */
  cmpDescribedBy: PropTypes.string,
  /** font size for the label of the button.  1= 14px, 2 = 18px (for now, see variables.less and bootstrap.less for details) */
  fontSizeType: PropTypes.oneOf([1, 2]),
}

export default AButtonStandard
